import { lazy, Suspense } from "react";
import { useAuth } from "store/useAuthStore";
import { useLocation } from "react-router-dom";
import PageLoader from "components/shared/loaders/PageLoader";
import EmptyState from "components/empty-states/EmptyState";
import {
  ROUTE_COMPONENT_MAP,
} from "utils/routeMappings";

/**
 * Wrapper component that dynamically renders the correct component based on route and role
 * This replaces the need for role-specific route nesting
 */
const DynamicRouteWrapper = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;

  if (loading) {
    return <PageLoader />;
  }
  
  if (!user || !user.role) {
    return (
      <EmptyState
        type="unauthorized"
        title="Authentication Required"
        message="Please log in to access this page"
      />
    );
  }

  const userRole = user.role;
  let Component = null;

  // Normalize pathname (handle query params, trailing slashes, but keep base path)
  const normalizedPath = pathname.split("?")[0].replace(/\/$/, "") || "/";
  
  // For nested routes, use parent path for component lookup
  // e.g., /orders/all -> use /orders, /orders/customer-orders/123 -> use /orders
  const basePath = normalizedPath.split("/").slice(0, 2).join("/") || normalizedPath;

  // Check exact match first
  if (ROUTE_COMPONENT_MAP[normalizedPath]) {
    Component = ROUTE_COMPONENT_MAP[normalizedPath][userRole];
  } else if (ROUTE_COMPONENT_MAP[basePath]) {
    // Check base path for nested routes
    Component = ROUTE_COMPONENT_MAP[basePath][userRole];
  } else {
    // Check pattern matches (for dynamic routes like /orders/:id)
    for (const [routePattern, roleComponents] of Object.entries(
      ROUTE_COMPONENT_MAP
    )) {
      const patternRegex = new RegExp(
        "^" + routePattern.replace(/:[^/]+/g, "[^/]+") + "$"
      );
      if (patternRegex.test(normalizedPath) || patternRegex.test(basePath)) {
        Component = roleComponents[userRole];
        if (Component) break;
      }
    }
  }

  if (!Component) {
    return (
      <EmptyState
        type="empty"
        title="Page Not Available"
        message={`This page is not available for ${userRole} role`}
      />
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
};

export default DynamicRouteWrapper;

